import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["error", "label", "FormLabel", "validationMessage", "help"];
/* eslint-disable @typescript-eslint/no-floating-promises */

import { useState, useCallback, memo, Fragment } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
// @ts-expect-error has no typings yet
import KnowledgeBaseButton from 'ui-addon-i18n/components/KnowledgeBaseButton';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { fetchIsCampaignAtLimit } from 'campaigns-lib/data/CampaignDao';
import CampaignSelect from 'campaigns-lib/components/CampaignSelect';
import { SUPPORTED_ASSET_OBJECTS_TYPES } from 'campaigns-lib/constants/campaignSelect';
import useGetCampaignSelectChangeHandler from 'campaigns-lib/hooks/useGetCampaignSelectChangeHandler';
import unescapedText from 'I18n/utils/unescapedText';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function CampaignSelectFormControlComponent({
  applicationId,
  assetObjectType,
  enableTracking,
  formControlProps = {},
  hasCampaignsReadAccess,
  hasCampaignsWriteAccess,
  multi,
  value,
  onChange,
  showMissingAccessError = false,
  editorSelect = false,
  assetLabel,
  formHelpAsParagraph = false,
  disabledTooltip,
  withCampaignAutoSelectPopover,
  disabledOptionsByName,
  'data-test-id': dataTestId,
  disabled,
  className,
  dropdownClassName,
  autoLoad,
  error,
  inputRef,
  objectBuilderUiProps
}) {
  const [notFoundError, setNotFoundError] = useState();
  const [limitError, setLimitError] = useState();
  const {
      error: formControlError,
      label: formControlLabel,
      FormLabel = Fragment,
      //TO-DO: Remove default value once FormLabel is fixed: https://git.hubteam.com/HubSpot/foundations-components/issues/3568
      validationMessage: formControlValidationMessage,
      help: formControlHelp
    } = formControlProps,
    restFormControlProps = _objectWithoutPropertiesLoose(formControlProps, _excluded);
  const handleCurrentValueNotFound = useCallback(() => setNotFoundError(true), []);
  const handleChange = useGetCampaignSelectChangeHandler({
    multi,
    onChange
  });
  const handleChangeWithLimitCheck = useCallback((...data) => {
    setNotFoundError(false);
    setLimitError(false);
    const [, optionOrOptions] = data;
    handleChange(optionOrOptions);
    if (assetObjectType) {
      const campaignOption = Array.isArray(optionOrOptions) ?
      // the newest campaign added is the last one
      optionOrOptions[optionOrOptions.length - 1] : optionOrOptions;
      if (campaignOption && campaignOption.guid) {
        fetchIsCampaignAtLimit({
          assetObjectType,
          campaignGuid: campaignOption.guid
        }).then(isAtLimit => {
          setLimitError(isAtLimit);
          if (isAtLimit) {
            if (multi && Array.isArray(optionOrOptions)) {
              handleChange(
              // re-triggering the change without that last campaign if multi select
              optionOrOptions.slice(0, optionOrOptions.length));
            } else {
              // unselecting the campaign if not multi select
              handleChange([]);
            }
          }
        }).catch();
      }
    }
  }, [assetObjectType, multi, handleChange]);
  const getValidationMessage = useCallback(() => {
    if (notFoundError && showMissingAccessError) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "campaignSelect.validationErrors.campaignNotFoundWithNoAccess"
      });
    } else if (notFoundError) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "campaignSelect.validationErrors.campaignNotFound"
      });
    } /*else if (!hasCampaignsWriteAccess) {
      return (
        <FormattedMessage message="campaignSelect.validationErrors.noWriteAccess" />
      );
      }*/else if (limitError && assetObjectType) {
      if (SUPPORTED_ASSET_OBJECTS_TYPES.indexOf(assetObjectType) > -1) {
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: `campaignSelect.validationErrors.limitError.${assetObjectType}`
        });
      } else {
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "campaignSelect.validationErrors.limitError.generic"
        });
      }
    }
    return null;
  }, [assetObjectType,
  // hasCampaignsWriteAccess,
  limitError, notFoundError, showMissingAccessError]);
  if (!hasCampaignsReadAccess) {
    return null;
  }
  if (editorSelect) {
    const help = formControlHelp || /*#__PURE__*/_jsx("span", {
      style: {
        whiteSpace: 'normal'
      },
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "campaignSelect.formEditorControlDescription_jsx",
        options: {
          className: 'p-all-0',
          style: formHelpAsParagraph ? {
            marginTop: '-3px'
          } : {
            marginTop: '-2px',
            fontSize: '12px'
          },
          url: 'https://knowledge.hubspot.com/campaigns/understand-campaigns',
          use: 'transparent',
          useZorse: true,
          assetLabel: assetLabel || I18n.text('campaignSelect.defaultAssetLabel')
        },
        elements: {
          Button: KnowledgeBaseButton
        }
      })
    });
    return /*#__PURE__*/_jsx(FormControl, Object.assign({
      error: notFoundError || limitError || formControlError /*||
                                                             !hasCampaignsWriteAccess*/,
      label:
      /*#__PURE__*/
      //TO-DO: Remove the FormLabel and Text wrap once the FormLabel bug is fixed: https://git.hubteam.com/HubSpot/foundations-components/issues/3568
      _jsx(FormLabel, {
        children: /*#__PURE__*/_jsx(Text, {
          as: "span",
          whiteSpace: "normal",
          children: formControlLabel || /*#__PURE__*/_jsx(FormattedMessage, {
            message: "campaignSelect.formEditorControlLabel"
          })
        })
      }),
      FormLabel: FormLabel || undefined,
      help: !formHelpAsParagraph && help,
      tooltipProps: {
        'data-test-id': 'campaign-select-form-control-tooltip'
      },
      validationMessage: getValidationMessage() || formControlValidationMessage,
      "data-test-id": "campaign-select-form-control"
    }, restFormControlProps, {
      children: /*#__PURE__*/_jsxs("span", {
        "data-test-id": "campaign-select-input-wrapper",
        children: [formHelpAsParagraph && help, /*#__PURE__*/_jsx(CampaignSelect, {
          assetObjectType: assetObjectType,
          onChange: handleChangeWithLimitCheck,
          onCurrentValueNotFound: handleCurrentValueNotFound,
          value: value,
          canCreateCampaigns: hasCampaignsWriteAccess,
          multi: multi,
          enableTracking: enableTracking !== undefined ? enableTracking : true,
          autoLoad: autoLoad || true,
          withEmptyState: true,
          disabledTooltip: disabledTooltip,
          withCampaignAutoSelectPopover: withCampaignAutoSelectPopover,
          disabledOptionsByName: disabledOptionsByName,
          applicationId: applicationId,
          uiSelectProps: {
            className: className || 'm-top-2',
            error: error || notFoundError || limitError /*|| !hasCampaignsWriteAccess*/,
            placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "campaignSelect.selectCampaign"
            }),
            dropdownClassName,
            'data-test-id': dataTestId,
            disabled,
            inputRef
          },
          objectBuilderUiProps: objectBuilderUiProps
        })]
      })
    }));
  }
  return /*#__PURE__*/_jsx(FormControl, {
    error: notFoundError || limitError || formControlError,
    label:
    /*#__PURE__*/
    //TO-DO: Remove the FormLabel and Text wrap once the FormLabel bug is fixed: https://git.hubteam.com/HubSpot/foundations-components/issues/3568
    _jsx(FormLabel, {
      children: /*#__PURE__*/_jsx(Text, {
        as: "span",
        whiteSpace: "normal",
        children: formControlLabel || /*#__PURE__*/_jsx(FormattedMessage, {
          message: "campaignSelect.formControlLabel"
        })
      })
    }),
    FormLabel: FormLabel || undefined,
    tooltip: unescapedText('campaignSelect.formControlTooltip'),
    validationMessage: getValidationMessage() || formControlValidationMessage,
    children: /*#__PURE__*/_jsx(CampaignSelect, {
      onChange: handleChangeWithLimitCheck,
      onCurrentValueNotFound: handleCurrentValueNotFound,
      value: value,
      canCreateCampaigns: hasCampaignsWriteAccess,
      multi: multi,
      enableTracking: enableTracking !== undefined ? enableTracking : true,
      autoLoad: autoLoad || true,
      disabledTooltip: disabledTooltip,
      withCampaignAutoSelectPopover: withCampaignAutoSelectPopover,
      disabledOptionsByName: disabledOptionsByName,
      applicationId: applicationId,
      uiSelectProps: {
        className,
        error: error || notFoundError || limitError,
        placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "campaignSelect.selectCampaign"
        }),
        dropdownClassName,
        'data-test-id': dataTestId,
        disabled,
        inputRef
      }
    })
  });
}
export default /*#__PURE__*/memo(CampaignSelectFormControlComponent);